import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, User, Briefcase, Phone, Tag, Mail, Info } from 'lucide-react';
import { saveUserProfile, getUserProfile, ProfileData, updateUserProfile } from '../../services/user.service';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/logo.gif';
import { customerApi } from '../../services/customerApi';

const UpdateProfileForm = () => {
  const navigate = useNavigate();
  const { user, profile, role, refreshUserProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasWaitlistData, setHasWaitlistData] = useState(false); // Track if data came from waitlist
  const [formData, setFormData] = useState<Partial<ProfileData>>({
    fullName: '',
    studioName: '',
    jobTitle: '',
    phoneNumber: '',
    email: '',
    productionType: '',
    project: ''
  });
  const [accountType, setAccountType] = useState<'personal' | 'work' | ''>('');

  // Show waitlist notice if user is waitlisted AND doesn't have pre-populated data
  const isWaitlisted = role === 'waitlisted';
  const shouldShowWaitlistUI = isWaitlisted && !hasWaitlistData;

  // Fetch any existing profile data or initialize with user data
  useEffect(() => {
    const initializeForm = async () => {
      // If no user, redirect to login
      if (!user) {
        navigate('/login');
        return;
      }

      // First check if we already have complete profile data in the context
      if (profile && (profile as any).fullName) {
        const { role: _accountRole, ...rest } = profile as any;
        let merged: any = rest;
        // If studioName is missing, try to enrich from waitlist profile
        if ((!merged.studioName || !String(merged.studioName).trim()) && user?.email) {
          try {
            const waitlistProfileResponse = await customerApi.getWaitlistProfile(user.email);
            if (waitlistProfileResponse && waitlistProfileResponse.success && waitlistProfileResponse.data) {
              const wl = waitlistProfileResponse.data as any;
              merged = {
                ...merged,
                studioName: wl.studioName || merged.studioName,
                ...(merged.jobTitle ? {} : { jobTitle: wl.jobTitle || '' })
              };
            }
          } catch {
            // swallow enrichment errors
          }
        }
        setFormData(merged);
        setAccountType((merged.studioName === 'Personal Use') ? 'personal' : 'work');
        setIsUpdateMode(true);
        setLoading(false);
        return;
      }

      // Otherwise try to fetch from API (profile table)
      try {
        const profileData = await getUserProfile();
        // Ignore server 'role' (account role) so it doesn't overwrite the job title field
        const { role: _accountRole, ...rest } = profileData as any;
        let merged: any = rest;
        if ((!merged.studioName || !String(merged.studioName).trim()) && user?.email) {
          try {
            const waitlistProfileResponse = await customerApi.getWaitlistProfile(user.email);
            if (waitlistProfileResponse && waitlistProfileResponse.success && waitlistProfileResponse.data) {
              const wl = waitlistProfileResponse.data as any;
              merged = {
                ...merged,
                studioName: wl.studioName || merged.studioName,
                ...(merged.jobTitle ? {} : { jobTitle: wl.jobTitle || '' })
              };
            }
          } catch {
            // continue without enrichment
          }
        }
        setFormData(merged);
        setAccountType((merged.studioName === 'Personal Use') ? 'personal' : 'work');
        setIsUpdateMode(true);
      } catch (error) {
        // If profile not found in profile table, fallback to whitelist/waitlist info
        console.log('No existing profile found in profile table, falling back');
        
        // Initialize with user email first
        let initialFormData: Partial<ProfileData> = { 
          email: user.email || ''
        };

        // Try to get waitlist profile data (by email) to prefill
        if (user.email) {
          console.log('User is waitlisted, checking for waitlist profile data...');
          try {
            const waitlistProfileResponse = await customerApi.getWaitlistProfile(user.email);
            
            console.log('Found waitlist profile response:', waitlistProfileResponse);
            
            if (waitlistProfileResponse && waitlistProfileResponse.success && waitlistProfileResponse.data) {
              const waitlistProfile = waitlistProfileResponse.data;
              
              // Map waitlist profile fields to regular profile fields
              initialFormData = {
                ...initialFormData,
                fullName: waitlistProfile.fullName || '',
                studioName: waitlistProfile.studioName || '',
                phoneNumber: waitlistProfile.phoneNumber || '',
                project: waitlistProfile.project || '',
                productionType: waitlistProfile.productionType || '',
                softwareTools: waitlistProfile.softwareTools || [],
                exportFormat: waitlistProfile.exportFormat || '',
                jobTitle: '' // Leave empty for user to fill in their job role/title
              };
              setAccountType((waitlistProfile.studioName === 'Personal Use') ? 'personal' : 'work');
              
              // Mark that we have waitlist data when role is waitlisted
              if (role === 'waitlisted') {
                setHasWaitlistData(true);
              }
              
              console.log('Pre-populated form with waitlist profile data:', initialFormData);
            } else {
              console.log('Waitlist profile response exists but no data found');
            }
          } catch (waitlistError) {
            console.info('No waitlist profile found. Proceeding with minimal defaults for direct sign-up.');
            console.debug('Waitlist lookup error details:', waitlistError);
            // Continue with basic initialization if waitlist profile fetch fails
          }
        }
        
        setFormData(initialFormData);
        console.log('Profile creation mode - form initialized with:', initialFormData);
      } finally {
        setLoading(false);
      }
    };

    initializeForm();
  }, [user, profile, navigate, role]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAccountTypeChange = (type: 'personal' | 'work') => {
    setAccountType(type);
    if (type === 'personal') {
      setFormData(prev => ({ ...prev, studioName: 'Personal Use' }));
      if (errors.studioName) setErrors(prev => ({ ...prev, studioName: '' }));
    } else {
      // Switching to work: clear auto value so field becomes editable & visible
      setFormData(prev => ({ ...prev, studioName: (prev.studioName === 'Personal Use' ? '' : (prev.studioName || '')) }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (accountType !== 'personal') {
      if (!formData.studioName?.trim()) {
        newErrors.studioName = 'Company/Studio name is required';
      }
    }
    
    if (!(formData as any).jobTitle?.trim()) {
      newErrors.jobTitle = 'Role / Title is required';
    }
    
    // Only require project description for waitlisted users without pre-populated data
    if (shouldShowWaitlistUI && !formData.project?.trim()) {
      newErrors.project = 'Project description is required for waitlisted users';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;
    if (!user) {
      setErrors(prev => ({ ...prev, submit: 'You are not signed in.' }));
      return;
    }
    setIsSubmitting(true);
    
    try {
      if (isUpdateMode) {
        // Update existing profile
        const { userId, ...updateData } = formData;
        await updateUserProfile(updateData);
      } else {
        await saveUserProfile(formData as ProfileData);

        localStorage.setItem('hasProfile', 'true');
        console.log('Profile created - set hasProfile to true in localStorage'); 
      }
      
      // Refresh profile in auth context
      await refreshUserProfile();
      
      // Navigate to dashboard after successful profile creation/update for all users
      navigate('/dashboard');
    } catch (error) {
      console.error('Profile save error:', error);
      setErrors(prev => ({ 
        ...prev, 
        submit: error instanceof Error ? error.message : 'An error occurred saving your profile'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161513] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0CB4E4]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161513] flex flex-col relative">
      {/* Logo Background */}
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
        style={{ backgroundImage: `url(${logo})` }}
      ></div>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md">
          <div className="bg-[#161513] rounded-none shadow-xl border border-[#0CB4E4]/30 overflow-hidden">
            <div className="p-6 border-b border-[#0CB4E4]/20 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">{isUpdateMode ? 'Update Your Profile' : 'Complete Your Profile'}</h2>
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-300 hover:text-white rounded-lg transition-colors hover:bg-[#161513]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pt-4">
              <p className="text-gray-300">Tell us more about yourself to get started</p>
            </div>
            {/* Account Type */}
            <form onSubmit={handleSubmit} className="p-6 md:p-8">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Account Type <span className="text-red-400">*</span></label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`flex items-center gap-3 p-3 border rounded-none cursor-pointer transition-all ${accountType === 'personal' ? 'border-[#0CB4E4] ring-1 ring-[#0CB4E4] bg-[#0CB4E4]/10 text-[#0CB4E4]' : 'border-[#0CB4E4]/30 hover:border-[#0CB4E4]/50 text-white'}`}>
                      <input type="radio" name="accountType" value="personal" checked={accountType==='personal'} onChange={() => handleAccountTypeChange('personal')} className="sr-only" />
                      <User className={`w-5 h-5 ${accountType==='personal' ? 'text-[#0CB4E4]' : 'text-gray-400'}`} />
                      <span className="text-sm font-medium">Personal Use</span>
                    </label>
                    <label className={`flex items-center gap-3 p-3 border rounded-none cursor-pointer transition-all ${accountType === 'work' ? 'border-[#0CB4E4] ring-1 ring-[#0CB4E4] bg-[#0CB4E4]/10 text-[#0CB4E4]' : 'border-[#0CB4E4]/30 hover:border-[#0CB4E4]/50 text-white'}`}>
                      <input type="radio" name="accountType" value="work" checked={accountType==='work'} onChange={() => handleAccountTypeChange('work')} className="sr-only" />
                      <Briefcase className={`w-5 h-5 ${accountType==='work' ? 'text-[#0CB4E4]' : 'text-gray-400'}`} />
                      <span className="text-sm font-medium">Work Use</span>
                    </label>
                  </div>
                </div>
                {/* spacer consistent with other fields */}
                <div className="h-0" />
                {/* Full Name */}
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-white mb-2">
                    Full Name <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      value={formData.fullName || ''}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-2.5 rounded-none bg-[#161513] border ${
                        errors.fullName ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                      } focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors text-white`}
                      placeholder="Your full name"
                    />
                  </div>
                  {errors.fullName && (
                    <p className="mt-2 text-sm text-red-400">{errors.fullName}</p>
                  )}
                </div>
                
                {/* Email (readonly) */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email || user?.email || ''}
                      readOnly
                      className="w-full pl-10 pr-4 py-2.5 rounded-none bg-[#161513] border border-[#0CB4E4]/30 text-gray-300 focus:outline-none transition-colors"
                    />
                  </div>
                </div>
                
                {/* Company / Studio Name */}
                <div className={`${accountType==='personal' ? 'opacity-60' : ''}`}>
                  <label htmlFor="studioName" className="block text-sm font-medium text-white mb-2">
                    Company / Studio Name {accountType==='work' && (<span className="text-red-400">*</span>)}
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      id="studioName"
                      name="studioName"
                      type="text"
                      value={formData.studioName || ''}
                      onChange={handleChange}
                      disabled={accountType==='personal'}
                      className={`w-full pl-10 pr-4 py-2.5 rounded-none bg-[#161513] border ${
                        errors.studioName ? 'border-red-500 bg-red-900/10' : (accountType==='work' ? 'border-[#0CB4E4]' : 'border-[#0CB4E4]/30')
                      } focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors text-white`}
                      placeholder="Your company or studio name"
                    />
                  </div>
                  {errors.studioName && (
                    <p className="mt-2 text-sm text-red-400">{errors.studioName}</p>
                  )}
                </div>
                
                {/* Role / Title */}
                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-white mb-2">
                    Role / Title <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      id="jobTitle"
                      name="jobTitle"
                      type="text"
                      value={(formData as any).jobTitle || ''}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-2.5 rounded-none bg-[#161513] border ${
                        errors.jobTitle ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                      } focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors text-white`}
                      placeholder="e.g. VFX Supervisor, 3D Artist"
                    />
                  </div>
                  {errors.jobTitle && (
                    <p className="mt-2 text-sm text-red-400">{errors.jobTitle}</p>
                  )}
                </div>
                
                {/* Phone Number - Optional */}
                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-white mb-2">
                    Phone Number <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      value={formData.phoneNumber || ''}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2.5 rounded-none bg-[#161513] border border-[#0CB4E4]/30 focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors text-white"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
                
                {/* Project Description - Show different labels based on waitlist status and data source */}
                <div>
                  <label htmlFor="project" className="block text-sm font-medium text-white mb-2">
                    What are you working on that requires 3D?
                    {shouldShowWaitlistUI ? (
                      <>
                        <span className="text-[#0CB4E4] font-medium"> *</span>
                        <span className="ml-1 inline-flex items-center">
                          <Info className="h-4 w-4 text-[#0CB4E4]" />
                          <span className="ml-1 text-xs text-[#0CB4E4]">Required for waitlist review</span>
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-400"> (optional)</span>
                    )}
                  </label>
                  <textarea
                    id="project"
                    name="project"
                    value={formData.project || ''}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-none bg-[#161513] border ${
                      errors.project ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                    } focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors text-white`}
                    placeholder={shouldShowWaitlistUI ? 
                      "Please provide details about your current projects, your experience, and how you plan to use our platform" : 
                      "Briefly describe your current project or use case"
                    }
                    rows={shouldShowWaitlistUI ? 4 : 2}
                  />
                  {errors.project && (
                    <p className="mt-2 text-sm text-red-400">{errors.project}</p>
                  )}
                </div>
                
                {/* Submit error */}
                {errors.submit && (
                  <div className="bg-red-900/20 border border-red-400/30 text-red-400 px-4 py-3 rounded-none text-sm">
                    {errors.submit}
                  </div>
                )}
              </div>
              
              <div className="mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 px-6 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-[#161513] font-medium rounded-none shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#161513]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <span>{isUpdateMode ? 'Update Profile' : 'Complete Profile'}</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
              
              {/* Additional information for waitlisted users - only show if no pre-populated data */}
              {shouldShowWaitlistUI && (
                <div className="mt-6 p-4 bg-[#0CB4E4]/10 rounded-none border border-[#0CB4E4]/30">
                  <h4 className="text-xs font-medium text-white">What happens next?</h4>
                  <ul className="mt-1 space-y-1 text-xs text-gray-300">
                    <li className="flex gap-1.5">
                      <div className="flex-shrink-0 h-4 w-4 text-[#0CB4E4] flex items-center justify-center rounded-full bg-[#0CB4E4]/20">
                        1
                      </div>
                      <span>Your profile information will be reviewed by our team</span>
                    </li>
                    <li className="flex gap-1.5">
                      <div className="flex-shrink-0 h-4 w-4 text-[#0CB4E4] flex items-center justify-center rounded-full bg-[#0CB4E4]/20">
                        2
                      </div>
                      <span>We'll notify you when your access is approved</span>
                    </li>
                  </ul>
                </div>
              )}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UpdateProfileForm;